import React, { useEffect, useState } from 'react';
import type { ChartSeries, DataChartBlock } from '../../lib/api/geologist';
import type { GeoUiLang } from './geoWorkspaceI18n';

// P7.4.2 — reusable animated chart (bar / grouped_bar / stacked_bar / donut / line).
// All values come from the backend (real data); this only draws them beautifully.

function fmt(n: number, lang: GeoUiLang): string {
  const suffix = lang === 'ru' ? [' млн', ' тыс'] : lang === 'hy' ? [' մլն', ' հազ.'] : ['M', 'K'];
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)}${suffix[0]}`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}${suffix[1]}`;
  return n.toLocaleString(lang === 'ru' ? 'ru-RU' : lang === 'hy' ? 'hy-AM' : 'en-US');
}

const useMount = () => {
  const [on, setOn] = useState(false);
  useEffect(() => { const t = setTimeout(() => setOn(true), 40); return () => clearTimeout(t); }, []);
  return on;
};

const Legend: React.FC<{ series: ChartSeries[] }> = ({ series }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8, fontSize: 12 }}>
    {series.map((s) => (
      <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--ink-secondary)' }}>
        <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color }} />{s.name}
      </span>
    ))}
  </div>
);

const StackedBar: React.FC<{ c: DataChartBlock; on: boolean; lang: GeoUiLang }> = ({ c, on, lang }) => {
  const totals = c.categories.map((_, i) => c.series.reduce((a, s) => a + (s.values[i] || 0), 0));
  const max = Math.max(1, ...totals);
  return (
    <div>
      {c.categories.map((cat, i) => (
        <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ width: 74, fontSize: 12, color: 'var(--ink-secondary)', textAlign: 'right',
            fontFamily: 'var(--font-mono)' }}>{cat}</div>
          <div style={{ flex: 1, height: 20, display: 'flex', borderRadius: 5, overflow: 'hidden',
            background: 'var(--bg-subtle)', width: on ? `${(totals[i] / max) * 100}%` : '0%',
            transition: 'width .6s cubic-bezier(.2,.8,.2,1)' }}>
            {c.series.map((s) => {
              const v = s.values[i] || 0;
              return v > 0 ? <div key={s.name} title={`${s.name}: ${fmt(v, lang)}`}
                style={{ width: `${(v / totals[i]) * 100}%`, background: s.color, height: '100%' }} /> : null;
            })}
          </div>
          <div style={{ width: 64, fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)',
            fontVariantNumeric: 'tabular-nums' }}>{fmt(totals[i], lang)}</div>
        </div>
      ))}
      <Legend series={c.series} />
    </div>
  );
};

const GroupedBar: React.FC<{ c: DataChartBlock; on: boolean; lang: GeoUiLang }> = ({ c, on, lang }) => {
  const max = Math.max(1, ...c.series.flatMap((s) => s.values.map((v) => v || 0)));
  return (
    <div>
      {c.categories.map((cat, i) => (
        <div key={cat} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginBottom: 3 }}>{cat}</div>
          {c.series.map((s) => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <div style={{ flex: 1, height: 16, background: 'var(--bg-subtle)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: on ? `${((s.values[i] || 0) / max) * 100}%` : '0%', height: '100%',
                  background: s.color, transition: 'width .6s ease' }} />
              </div>
              <div style={{ width: 66, fontSize: 12, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt(s.values[i] || 0, lang)}</div>
            </div>
          ))}
        </div>
      ))}
      <Legend series={c.series} />
    </div>
  );
};

const Donut: React.FC<{ c: DataChartBlock; on: boolean; lang: GeoUiLang }> = ({ c, on, lang }) => {
  // The backend builds a donut as N series, each carrying ONE value at index 0
  // (chart_blocks.py: ChartSeries(name=cat[i], values=[val[i]])). Read the value
  // from the matching series — reading series[0].values[i] silently zeroed every
  // slice past the first and mis-totalled the ring (e.g. reserves by mark).
  const parts = c.categories.map((cat, i) => ({
    cat,
    v: c.series[i]?.values?.[0] ?? 0,
    color: c.series[i]?.color || c.series[0]?.color,
  }));
  const total = parts.reduce((a, p) => a + p.v, 0) || 1;
  const R = 62, C = 2 * Math.PI * R;
  const segments = parts.map((part, index) => ({
    ...part,
    offset: parts.slice(0, index).reduce((sum, previous) => sum + previous.v, 0) / total,
  }));
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg width={160} height={160} viewBox="0 0 160 160">
        <g transform="rotate(-90 80 80)">
          {segments.map((p, idx) => {
            const frac = p.v / total;
            const dash = on ? frac * C : 0;
            return (
              <circle key={idx} cx="80" cy="80" r={R} fill="none" stroke={p.color} strokeWidth="20"
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-p.offset * C}
                style={{ transition: 'stroke-dasharray .7s ease' }} />
            );
          })}
        </g>
        <text x="80" y="78" textAnchor="middle" fontSize="12" fill="var(--ink-tertiary)">{lang === 'ru' ? 'всего' : lang === 'hy' ? 'ընդամենը' : 'total'}</text>
        <text x="80" y="94" textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--ink-primary)">{fmt(total, lang)}</text>
      </svg>
      <div style={{ fontSize: 13 }}>
        {parts.filter((p) => p.v > 0).map((p) => (
          <div key={p.cat} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: p.color }} />
            <span style={{ color: 'var(--ink-secondary)' }}>{p.cat}</span>
            <span style={{ color: 'var(--ink-primary)', fontWeight: 600 }}>{fmt(p.v, lang)}</span>
            <span style={{ color: 'var(--ink-tertiary)' }}>({Math.round((p.v / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DataChart: React.FC<{ c: DataChartBlock; lang?: GeoUiLang }> = ({ c, lang = 'ru' }) => {
  const on = useMount();
  return (
    <div data-testid="preview-chart" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 8, padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--ink-tertiary)',
        marginBottom: 12, fontWeight: 600 }}>{c.title}{c.unit ? ` · ${c.unit}` : ''}</div>
      {c.kind === 'stacked_bar' && <StackedBar c={c} on={on} lang={lang} />}
      {c.kind === 'grouped_bar' && <GroupedBar c={c} on={on} lang={lang} />}
      {(c.kind === 'bar' || c.kind === 'line') && <GroupedBar c={c} on={on} lang={lang} />}
      {c.kind === 'donut' && <Donut c={c} on={on} lang={lang} />}
      {c.note && <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 10 }}>{c.note}</div>}
    </div>
  );
};

export default DataChart;
